import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BannerComponent } from '../components/banner/banner.component';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputTextModule } from 'primeng/inputtext';
import { RecruitmentPerfComponent } from '../components/sections/recruitment-perf/recruitment-perf.component';
import { JobOfferTrackingComponent } from '../components/sections/job-offer-tracking/job-offer-tracking.component';
import { ProcessEfficiencyComponent } from '../components/sections/process-efficiency/process-efficiency.component';
import { User } from '@job-v2/ats-utils';
import { AppGlobalService } from '../../commons/services/app-global.service';
import { Subject, takeUntil } from 'rxjs';
import { CompaniesService } from '../../settings/companies/companies.service';
import { DashboardTranslateKeys } from '../utils/dashboard-translate-keys.enum';
import { TranslateModule } from '@ngx-translate/core';
import { JOB_DASHBOARD_ACCESS } from '@socium/common-api';
import { AppCanDirective } from '@socium/sociumflow';
import { MultiSelectModule } from 'primeng/multiselect';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../auth/auth.service';

@Component({
  imports: [
    BannerComponent,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    RecruitmentPerfComponent,
    JobOfferTrackingComponent,
    ProcessEfficiencyComponent,
    TranslateModule,
    AppCanDirective,
    MultiSelectModule,
    FormsModule,
    IftaLabelModule
  ],
  selector: 'app-dashboard-rh',
  templateUrl: './dashboard-rh.component.html',
  styleUrl: './dashboard-rh.component.scss',
})
export class DashboardRhComponent implements OnInit, OnDestroy {
  public user!: User;
  public authService = inject(AuthService);
  readonly appGlobalService = inject(AppGlobalService);
  public DashboardTranslateKeys = DashboardTranslateKeys;
  private readonly destroy$ = new Subject<void>();
  dashboardAccess = JOB_DASHBOARD_ACCESS;
  public employees: User[] = [];
  public selectedJobOwners: string[] = [];
  private readonly companiesService = inject(CompaniesService);
  canSelectJobOwner = false;


  ngOnInit(): void {
    this.appGlobalService.currentUser
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user) {
          this.user = user;
          this.selectedJobOwners = this.authService.isRecruiter ? [this.user._id as string] : [];
          this.canSelectJobOwner = !this.authService.isRecruiter;
        }
      });
    this.loadEmployees();
  }

  loadEmployees(searchValue?: string): void {
    if (this.canSelectJobOwner) {
      const preservedSelectedEmployees = this.selectedJobOwners.length > 0 
        ? this.employees.filter(emp => this.selectedJobOwners.includes(emp.id))
        : [];
      
      this.companiesService.getCompanyRecruiters(this.user?.company?._id as string)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data: User[]) => {
            if (data) {
              let recruitersFromApi = data.map((recruiter: User) => ({
                ...recruiter,
                displayName: `${recruiter.firstName} ${recruiter.lastName}`,
                id: recruiter.id || recruiter._id
              })) as User[];
              
              if (searchValue) {
                recruitersFromApi = recruitersFromApi.filter((recruiter: User) => {
                  const fullName = `${recruiter.firstName} ${recruiter.lastName}`.toLowerCase();
                  return fullName.includes(searchValue.toLowerCase());
                });
              }

              if (preservedSelectedEmployees.length > 0) {
                const validExistingEmployees = preservedSelectedEmployees.filter(emp => 
                  emp && typeof emp === 'object' && emp.id
                );

                const existingEmployeeIds = validExistingEmployees.map(emp => emp.id);
                const mergedEmployees = [...validExistingEmployees];
                
                recruitersFromApi.forEach(emp => {
                  const employeeId = emp.id;
                  if (!existingEmployeeIds.includes(employeeId)) {
                    mergedEmployees.push(emp);
                  }
                });

                this.employees = mergedEmployees;
              } else {
                this.employees = recruitersFromApi;
              }
            }
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
